import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, Outlet, useNavigate, useLocation, useParams } from "react-router-dom";

import createMatch from "core/src/core/match.js";

import type { Match, MatchConfig } from "../shared/types";

import * as windowFocusHandler from "../shared/window-focus-handler.js";
import { parseEvent, isReservedKey } from "../shared/key-util.js";
import * as clientStateFunctions from "../shared/client-state-functions.js";
import GameOverlay from "../game-canvas/overlays/overlay-controller.js";

import LocalSetupPage from "../pages/setup/LocalSetupPage";
import CustomLobbyPage from "../pages/setup/CustomLobbyPage";
import JoinLobbyPage from "../pages/setup/JoinLobbyPage";
import GameComponent from "../pages/play/game/LocalGame";
import RemoteGame from "../pages/play/game/RemoteGame";
import Toast from "./Toast";
import InstallPrompt from "./InstallPrompt";
import MatchOverComponent from "../pages/play/match-over/MatchOver";
import MatchReplay from "../pages/play/replay/MatchReplay";
import ReplayPage from "../pages/play/replay/ReplayPage";
import GameOverComponent from "../pages/play/game-over/GameOver";

import NavBar, { ShellSection } from "./NavBar";
import Footer from "./Footer";
import AboutPage from "../pages/about/AboutPage";
import { applySettings, getSettings } from "../shared/settings";
import { enterFullscreen } from "../shared/fullscreen";
import FullscreenNudge from "./FullscreenNudge";
import RotateScreen from "./RotateScreen";
import musicController from "../audio/music-controller";
import { recordMatch } from "../shared/stats";
import { markMatchFinished } from "../shared/played";
import HelpPage from "../shared/help-modal/HelpPage";
import LoginModal from "./LoginModal";
import { useAuth } from "../shared/auth";
import LobbyPage from "../pages/lobby/LobbyPage";
import OnlineMatchPage from "../pages/lobby/OnlineMatchPage";
import RankedMatchPage from "../pages/ranked/RankedMatchPage";
import PlayNowPage from "../pages/modes/PlayNowPage";
import RoyalePage from "../pages/modes/RoyalePage";
import { claimGuestRoyaleResult, heldGuestRoyaleResult } from "../shared/royale";
import { refreshEggBalance } from "../shared/shop/useEggBalance";
import { showToast } from "../shared/toast";
import PlayNowLayerLab from "../pages/debug/PlayNowLayerLab";
import OpenAchievementsLab from "../pages/debug/OpenAchievementsLab";
import RankBadgesSheet from "../pages/debug/RankBadgesSheet";
import RoyaleCardsSheet from "../pages/debug/RoyaleCardsSheet";
import WhoPlaysSheet from "../pages/debug/WhoPlaysSheet";
import PhonePlayerSheet from "../pages/debug/PhonePlayerSheet";
import RoyaleUxSheet from "../pages/debug/RoyaleUxSheet";
import RoyaleFlowSheet from "../pages/debug/RoyaleFlowSheet";
import RoomDesignsSheet from "../pages/debug/RoomDesignsSheet";
import RoomFramesSheet from "../pages/debug/RoomFramesSheet";
import RoyaleLayerLab from "../pages/debug/RoyaleLayerLab";
import NewsPage from "../pages/news/NewsPage";
import NewsPostPage from "../pages/news/NewsPostPage";
import LeaderboardPage from "../pages/leaderboard/LeaderboardPage";
import ProfilePage from "../pages/profile/ProfilePage";
import ShopPage from "../pages/shop/ShopPage";
import ShopTryPage from "../pages/shop/ShopTryPage";
import SettingsPage from "../pages/settings/SettingsPage";
import PrivacyPage from "../pages/legal/PrivacyPage";
import TermsPage from "../pages/legal/TermsPage";
import SectionModal from "./SectionModal";
import { DebugGame, DebugRoundOver, DebugMatchOver, DebugRanks, DebugMatchEnd } from "../pages/debug/DebugViews";
import DebugMatches from "../pages/debug/DebugMatches";
import DebugNetPage from "../pages/debug/net/DebugNetPage";
import DebugBotsPage from "../pages/debug/DebugBotsPage";
import PlaygroundPage from "../pages/playground/PlaygroundPage";
import RankedLobbyLab from "../pages/debug/RankedLobbyLab";
import DuelDotsLab from "../pages/debug/DuelDotsLab";
import DuelBoardLab from "../pages/debug/DuelBoardLab";
// The start-page design lab is big (29 mockups): load it only when its page is
// opened, so it never weighs down the app players download.
const StartDesignsLab = React.lazy(() => import("../pages/debug/start-designs/StartDesignsLab"));
// The sound lab, likewise: only loaded when its page is opened.
const SoundLab = React.lazy(() => import("../pages/debug/sound-lab/SoundLab"));
const NavbarLab = React.lazy(() => import("../pages/debug/navbar-lab/NavbarLab"));
const BrandLab = React.lazy(() => import("../pages/debug/brand-lab/BrandLab"));
const RoyaleEndLab = React.lazy(() => import("../pages/debug/royale-end/RoyaleEndLab"));
const FirstMinuteLab = React.lazy(() => import("../pages/debug/first-minute/FirstMinuteLab"));
const TimingsLab = React.lazy(() => import("../pages/debug/timings-lab/TimingsLab"));
const RoyaleDesigns = React.lazy(() => import("../pages/debug/royale-designs/RoyaleDesigns"));
const ProgressionDesigns = React.lazy(() => import("../pages/debug/progression-designs/ProgressionDesigns"));
const TodayDesigns = React.lazy(() => import("../pages/debug/today-designs/TodayDesigns"));
const ProgressLab = React.lazy(() => import("../pages/debug/progress-lab/ProgressLab"));
const PortalLab = React.lazy(() => import("../pages/debug/portal-lab/PortalLab"));
const AchievementDesigns = React.lazy(() => import("../pages/debug/achievement-designs/AchievementDesigns"));
const WormSkinsLab = React.lazy(() => import("../pages/debug/worm-skins/WormSkinsLab"));
const CrownLab = React.lazy(() => import("../pages/debug/crown-lab/CrownLab"));
const RoyaleCardLab = React.lazy(() => import("../pages/debug/royale-card/RoyaleCardLab"));
const StageNotices = React.lazy(() => import("../pages/debug/stage-notices/StageNotices"));
import DuelRoundWinLab from "../pages/debug/DuelRoundWinLab";
import FfaFindingLab from "../pages/debug/FfaFindingLab";
import FfaBannerLab from "../pages/debug/FfaBannerLab";
import EffectBarsDemo from "../pages/dev/EffectBarsDemo";
import PowerUpDesignsDemo from "../pages/dev/PowerUpDesignsDemo";
import WallHackDesignsDemo from "../pages/dev/WallHackDesignsDemo";
import PowerUpAnimDemo from "../pages/dev/PowerUpAnimDemo";
import FlagStylesDemo from "../pages/dev/FlagStylesDemo";
import PlayerBarDemo from "../pages/dev/PlayerBarDemo";
import SwitchAnimDemo from "../pages/dev/SwitchAnimDemo";
import SpawnAnimDemo from "../pages/dev/SpawnAnimDemo";
import FlagCardDemo from "../pages/dev/FlagCardDemo";
import NoveltyFlagsDemo from "../pages/dev/NoveltyFlagsDemo";
import AdminPage from "../pages/admin/AdminPage";
import BotLeaderboardPage from "../pages/admin/BotLeaderboardPage";
import MarketingPage from "../pages/admin/MarketingPage";
import ReplaysPage from "../pages/admin/ReplaysPage";
import StatsPage from "../pages/admin/StatsPage";
import ReplayWatchPage from "../pages/admin/ReplayWatchPage";
import { isAdmin } from "../shared/admin";
import { track } from "../shared/analytics";
import { noteMatchStarted } from "../shared/first-run";
import { markResuming, resumableGame } from "../online/resume";
import { restoreSticky } from "../online/net";
import { ON_PORTAL } from "../shared/portal-env";

// Steps of a live match. These live under the single /play route and are driven by
// setState (not the URL) because every step depends on the in-memory `Match` object
// and is not meant to be deep-linked, refreshed into, or shared. Local-game setup is
// NOT here — it needs no Match, so it gets its own addressable route (LOCAL_PATH).
type FlowView = "game" | "game-over" | "match-over" | "replay-round";
type Origin = "offline" | "online";

// Local-game setup landing (addressable, needs no Match) and the live-match route.
const LOCAL_PATH = "/local";
// Custom-lobby setup screens (full-page, like local). Create/Join share the same
// design + setup library as local play.
const CREATE_PATH = "/create";
const LOBBY_PATH = "/lobby";
const JOIN_PATH = "/join";
const PLAY_PATH = "/play";
// Sign-in and the signed-in account panel are section routes too, but not
// main-nav links (they're the nav-right action / avatar).
const LOGIN_PATH = "/login";
// /account (the old avatar modal) now lives at /profile; keep the old URL working.
const ACCOUNT_PATH = "/account";

// All shell sections are real routes; this maps each to its URL and back.
// The admin hub's sub-page (the bot leaderboard): part of the "admin" section.
const ADMIN_BOTS_PATH = "/admin/bots";
// The admin hub's marketing shelf: also part of the "admin" section.
const ADMIN_MARKETING_PATH = "/admin/marketing";
// The admin hub's play stats: also part of the "admin" section.
const ADMIN_STATS_PATH = "/admin/stats";
const SECTION_PATH: Record<ShellSection, string> = {
  lobby: "/",
  local: LOCAL_PATH,
  news: "/news",
  about: "/about",
  leaderboard: "/leaderboard",
  shop: "/shop",
  profile: "/profile",
  settings: "/settings",
  help: "/help",
  admin: "/admin",
};

function pathToSection(pathname: string): ShellSection | null {
  // A prerendered page loads at its slashed URL (/about/, /help/: Cloudflare
  // Pages serves dist/<route>/index.html there), the app links to the bare one.
  if (pathname.length > 1 && pathname.endsWith("/")) pathname = pathname.slice(0, -1);
  if (pathname === SECTION_PATH.lobby) return "lobby";
  if (pathname === SECTION_PATH.local) return "local";
  if (pathname === SECTION_PATH.news || pathname.startsWith("/news/")) return "news";
  if (pathname === SECTION_PATH.about) return "about";
  if (pathname === SECTION_PATH.leaderboard) return "leaderboard";
  if (pathname === SECTION_PATH.shop) return "shop";
  if (pathname === SECTION_PATH.profile || pathname.startsWith(SECTION_PATH.profile + "/")) return "profile";
  if (pathname === SECTION_PATH.settings) return "settings";
  if (pathname === SECTION_PATH.help) return "help";
  if (pathname === SECTION_PATH.admin || pathname === ADMIN_BOTS_PATH || pathname === ADMIN_MARKETING_PATH || pathname === ADMIN_STATS_PATH) return "admin";
  return null; // /login, /privacy, /terms, … — no nav link highlighted
}

// Shared chrome (NavBar + Footer) for the shell sections. The NavBar/Footer stay
// mounted across every section, so navigating between them never reloads the
// chrome. The lobby renders only on the Play route; opening a section unmounts it
// (its previews stop) and shows the section panel. Closing a section — back
// arrow, Escape, or a click outside — routes back to Play, which remounts the
// lobby. The Local section renders as a full page inside <main> (no panel).
function Shell({ onPlayOffline, onStartOnline }: { onPlayOffline: () => void; onStartOnline: (cfg: any) => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  // The nav's Sign in opens the login dialog OVER whatever page is showing
  // (no route change, so you stay put; OAuth also returns you right here).
  const [loginOpen, setLoginOpen] = useState(false);

  // Keep the auth routes consistent with auth state: a signed-in user has
  // nothing to do on /login (e.g. back from an OAuth redirect, or an old
  // bookmark), and a signed-out user (incl. just-logged-out) has nothing on
  // /account — bounce both to lobby. Signing in also closes the dialog.
  // The signed-OUT bounces wait for the session check: during it `user` is
  // still null even for a signed-in visitor, and bouncing then would eject a
  // signed-in reload of /profile to the lobby.
  useEffect(() => {
    if (user && location.pathname === LOGIN_PATH) navigate(SECTION_PATH.lobby);
    if (user) setLoginOpen(false);
    // (Profile stays: signed out it is the way to sign in, ProfilePage.)
    if (!authLoading && !user && location.pathname === ACCOUNT_PATH) navigate(SECTION_PATH.lobby);
    // A game site's build has no sign-in, Store, Ranked or account of ours
    // (shared/portal-env.ts): a way there that was missed lands on the start.
    if (ON_PORTAL && /^\/(login|account|shop|ranked)(\/|$)/.test(location.pathname)) navigate(SECTION_PATH.lobby, { replace: true });
  }, [user, authLoading, location.pathname, navigate]);

  // Local / Create / Join are full-page setup screens rendered inside <main>
  // (not modals), reached from the lobby rather than their own nav entry — keep
  // "Play" highlighted there.
  const isFullPage = location.pathname === SECTION_PATH.local || location.pathname.startsWith(LOBBY_PATH + "/") || location.pathname.startsWith(CREATE_PATH + "/") || location.pathname === JOIN_PATH;
  const navActive = isFullPage ? "lobby" : pathToSection(location.pathname);
  // A section modal (news/leaderboard/settings) is open on any non-lobby,
  // non-full-page shell path; hide the lobby so it isn't visible behind it.
  const sectionOpen = !isFullPage && location.pathname !== SECTION_PATH.lobby;

  return (
    <div className={"app-shell" + (isFullPage ? " app-shell--fullpage" : "")}>
      <NavBar
        active={navActive}
        onNavigate={(s) => navigate(SECTION_PATH[s])}
        onLogin={() => setLoginOpen(true)}
        onAccount={() => navigate(SECTION_PATH.profile)}
      />
      <main className={"app-main" + (isFullPage ? " app-main--wide" : "")}>
        {/* The lobby renders only on the Play route. When a section page is open
            it's unmounted (not just hidden) so its live previews stop running —
            the section panel is what you see. .app-main's flex:1 keeps the footer
            pinned to the bottom even with the lobby gone. */}
        {!isFullPage && !sectionOpen && (
          <LobbyPage onPlayOffline={onPlayOffline} onStartOnline={onStartOnline} onLogin={() => setLoginOpen(true)} />
        )}
        {/* Local / Create / Join render inline here (full page, not a modal). */}
        {isFullPage && <Outlet />}
      </main>
      <Footer />
      {/* Section pages (news / leaderboard / settings / about / legal) + help/login
          render as panels via position:fixed over the (now empty) play area. */}
      {!isFullPage && <Outlet />}
      {loginOpen ? <LoginModal dim onClose={() => setLoginOpen(false)} /> : null}
      <FullscreenNudge />
    </div>
  );
}

// /profile/<agentId>: the panel gets its tab band only when the viewer may
// see the private tabs (the player themselves, or an admin); otherwise it is
// the plain single-page panel.
function PlayerProfileRoute({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const { agentId } = useParams<{ agentId: string }>();
  const tabbed = (!!user && agentId === user.id) || isAdmin(user);
  return (
    <SectionModal title="Profile" tabbed={tabbed} eggs onClose={onClose}>
      <ProfilePage />
    </SectionModal>
  );
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // The app (or the site) starts on the start page while a game it was in may
  // still be on (closed by accident, not left): straight back into it
  // (online/resume.ts; the game page goes home again if it is gone).
  useEffect(() => {
    if (location.pathname !== "/") return;
    const back = resumableGame();
    if (!back || back.path === "/") return;
    restoreSticky(back.room || undefined, back.instance, back.origin);
    markResuming();
    navigate(back.path, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // A guest who played a Royale and then signed in keeps the result (and its
  // win): the token waiting in this tab is handed in as soon as there is an
  // account (shared/royale.ts). A sign-in through a provider leaves the page and
  // comes back, so this lives here, above every route.
  useEffect(() => {
    if (!user || !heldGuestRoyaleResult()) return;
    claimGuestRoyaleResult().then((kept) => {
      if (kept) { showToast("Your Royale result is saved to your account."); refreshEggBalance(); }
    });
  }, [user]);

  const [match, setMatch] = useState<Match | null>(null);
  const [replayRoundId, setReplayRoundId] = useState<number | undefined>(undefined);
  const [origin, setOrigin] = useState<Origin>("offline");
  const [flowView, setFlowView] = useState<FlowView>("game");

  const overlayRef = useRef<any>(null);
  if (!overlayRef.current) overlayRef.current = GameOverlay();
  // The live (offline) game handed from a finished round to the game-over screen, which
  // keeps running it as a cosmetic victory lap during the "next round" countdown.
  const lapGameRef = useRef<any>(null);
  // Which flow step to return to after a replay (replays are launched from match-over).
  const previousFlowRef = useRef<FlowView>("match-over");

  // Audio plays during a live round and while watching a replay (both feed the
  // controller an audio provider); every other screen is silent. The controller
  // fades the track in/out, and it only sounds after the user's first interaction
  // (browsers block audio before a gesture).
  const audibleView =
    location.pathname === PLAY_PATH && (flowView === "game" || flowView === "replay-round");

  useEffect(() => {
    musicController.setPlaying(audibleView);
  }, [audibleView]);

  useEffect(() => {
    applySettings(getSettings());
    musicController.init();
    windowFocusHandler.startListening();
    function onKeyDown(event: KeyboardEvent) {
      if (clientStateFunctions.isInputElementActive()) return;
      const newKey = parseEvent(event);
      if (isReservedKey(newKey)) event.preventDefault();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Move to a match-flow step, navigating onto the /play route if we aren't already there.
  function goToFlow(next: FlowView) {
    // Reaching match-over means a match was played through: unlock engagement
    // nudges (the install prompt waits for this). Fullscreen stays: it lasts
    // for the visit, not just the match (shared/fullscreen.ts).
    if (next === "match-over") markMatchFinished();
    setFlowView(next);
    if (location.pathname !== PLAY_PATH) navigate(PLAY_PATH);
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  }

  function startMatch(matchConfig: MatchConfig, originArg: Origin = origin) {
    setMatch(createMatch({ matchConfig }));
    setOrigin(originArg);
    // Online is tracked in RemoteGame instead: this runs when you enter the
    // lobby, which is not the same as a match actually starting. Offline has no
    // such gap, so the start gesture is the honest moment.
    if (originArg !== "online") {
      track("Game Started", { mode: "local", players: matchConfig.players.length });
      noteMatchStarted(); // the first-run hints' count (first-run.ts)
    }
    // Synchronous in the start gesture's call stack so the browser allows it.
    if (getSettings().autoFullscreen) enterFullscreen();
    goToFlow("game");
  }

  // Leaving the match flow: online players return to the lobby, offline players to setup.
  function goHome() {
    navigate(origin === "online" ? SECTION_PATH.lobby : LOCAL_PATH);
  }

  // The live match flow lives at /play and is rendered entirely from the in-memory Match.
  // Every step here requires one, so without it we bounce home — this is what a hard
  // refresh on /play hits, since the Match object doesn't survive a reload.
  function renderPlayFlow(): React.ReactNode {
    if (!match) return <Navigate to={SECTION_PATH.lobby} replace />;

    let page: React.ReactNode = null;
    if (flowView === "game") {
      page = (
        <GameComponent
          match={match}
          overlay={overlayRef.current}
          onGameOverAction={(lapGame?: any) => {
            lapGameRef.current = lapGame || null;
            goToFlow("game-over");
          }}
          onLeaveAction={goHome}
          isLocal={origin === "offline"}
        />
      );
    } else if (flowView === "replay-round") {
      page = (
        <MatchReplay
          match={match}
          initialRound={replayRoundId}
          onExit={() => goToFlow(previousFlowRef.current)}
          youIds={match.matchConfig.players.filter((p: any) => p.type === "human").map((p: any) => p.id)}
        />
      );
    } else if (flowView === "game-over") {
      page = (
        <GameOverComponent
          match={match}
          overlay={overlayRef.current}
          lapGame={lapGameRef.current}
          onStartNextGameAction={() => { lapGameRef.current = null; goToFlow("game"); }}
          onMatchOverAction={() => {
            lapGameRef.current = null;
            if (match && match.isMatchOver && match.isMatchOver()) recordMatch(match);
            if (origin !== "online") {
              track("Match Finished", { mode: "local", rounds: match?.matchState?.roundsData?.length ?? 0 });
            }
            goToFlow("match-over");
          }}
          onLeaveAction={goHome}
          isLocal={origin === "offline"}
        />
      );
    } else if (flowView === "match-over") {
      page = (
        <MatchOverComponent
          match={match}
          onRoundClick={(id: number) => {
            setReplayRoundId(id);
            previousFlowRef.current = "match-over";
            goToFlow("replay-round");
          }}
          onExitAction={goHome}
          onPlayAgain={() => startMatch(match.matchConfig, origin)}
        />
      );
    }

    return <div className="page-main page-main-game">{page}</div>;
  }

  return (
    <>
    <RotateScreen />
    <Toast />
    <InstallPrompt />
    <Routes>
      <Route
        element={
          <Shell
            onPlayOffline={() => navigate(LOCAL_PATH)}
            onStartOnline={(cfg: any) => startMatch(cfg, "online")}
          />
        }
      >
        {/* Lobby is rendered by Shell as the backdrop; "/" adds no modal. */}
        <Route path={SECTION_PATH.lobby} element={null} />
        {/* Local setup: full page inside the shell, not a modal. */}
        <Route
          path={LOCAL_PATH}
          element={
            <LocalSetupPage
              onStartMatch={(cfg: MatchConfig) => startMatch(cfg, "offline")}
              onBack={() => navigate(SECTION_PATH.lobby)}
            />
          }
        />
        {/* The live custom lobby at /lobby/CODE: one shareable URL for host and
            guests (adaptive on who's host). The invite link + QR point here.
            /create/:code is a back-compat alias for early invite links; /join is
            the browse/paste finder that forwards to /lobby/CODE. */}
        <Route path={LOBBY_PATH + "/:code"} element={<CustomLobbyPage onBack={() => navigate(SECTION_PATH.lobby)} />} />
        <Route path={CREATE_PATH + "/:code"} element={<CustomLobbyPage onBack={() => navigate(SECTION_PATH.lobby)} />} />
        <Route path={JOIN_PATH} element={<JoinLobbyPage onBack={() => navigate(SECTION_PATH.lobby)} />} />
        <Route
          path={SECTION_PATH.news}
          element={<SectionModal title="News" onClose={() => navigate(SECTION_PATH.lobby)}><NewsPage /></SectionModal>}
        />
        {/* Single post — its own URL (prerendered with NewsArticle JSON-LD); the
            panel's back arrow returns to the News index, not the lobby. */}
        <Route
          path="/news/:slug"
          element={<SectionModal title="News" onClose={() => navigate(SECTION_PATH.news)}><NewsPostPage /></SectionModal>}
        />
        <Route
          path={SECTION_PATH.leaderboard}
          element={<SectionModal title="Leaderboard" flush onClose={() => navigate(SECTION_PATH.lobby)}><LeaderboardPage /></SectionModal>}
        />
        <Route
          path={SECTION_PATH.shop}
          element={<SectionModal title="Store" tabbed eggs onClose={() => navigate(SECTION_PATH.lobby)}><ShopPage /></SectionModal>}
        />
        <Route
          path={SECTION_PATH.profile}
          element={<SectionModal title="Profile" tabbed eggs onClose={() => navigate(SECTION_PATH.lobby)}><ProfilePage /></SectionModal>}
        />
        {/* Any player's profile, from the leaderboard. The Overview is public;
            the tabs (Matches, Achievements) show for the player and admins, so
            the panel is only "tabbed" then. Back goes to the leaderboard. */}
        <Route path={SECTION_PATH.profile + "/:agentId"} element={<PlayerProfileRoute onClose={() => navigate(SECTION_PATH.leaderboard)} />} />
        <Route
          path={SECTION_PATH.settings}
          element={<SectionModal title="Settings" onClose={() => navigate(SECTION_PATH.lobby)}><SettingsPage /></SectionModal>}
        />
        <Route
          path={SECTION_PATH.help}
          element={<SectionModal title="How to play" onClose={() => navigate(SECTION_PATH.lobby)}><HelpPage /></SectionModal>}
        />
        {/* Admin-only tools hub, shown as a section modal like the others. Gated
            client-side; non-admins are bounced to the lobby. */}
        <Route
          path={SECTION_PATH.admin}
          element={
            isAdmin(user) ? (
              <SectionModal title="Admin" wide onClose={() => navigate(SECTION_PATH.lobby)}>
                <AdminPage />
              </SectionModal>
            ) : (
              <Navigate to={SECTION_PATH.lobby} replace />
            )
          }
        />
        {/* Admin-only: stored online matches. The list lives here; the viewer is a
            full-screen game page, routed outside the shell below. Server-gated too. */}
        <Route
          path="/admin/replays"
          element={
            isAdmin(user) ? (
              <SectionModal title="Replays" wide pageScroll onClose={() => navigate(SECTION_PATH.admin)}>
                <ReplaysPage />
              </SectionModal>
            ) : (
              <Navigate to={SECTION_PATH.lobby} replace />
            )
          }
        />
        {/* Admin-only: the bots' own leaderboard, a tab per crowd level. The data
            is server-gated too (the rating rows are hidden from clients). */}
        <Route
          path={ADMIN_BOTS_PATH}
          element={
            isAdmin(user) ? (
              <SectionModal title="Bot management" tabbed wide pageScroll onClose={() => navigate(SECTION_PATH.admin)}>
                <BotLeaderboardPage />
              </SectionModal>
            ) : (
              <Navigate to={SECTION_PATH.lobby} replace />
            )
          }
        />
        {/* Admin-only: who is playing right now, and games and players per day.
            The data is server-gated (the play log has no read policy). */}
        <Route
          path={ADMIN_STATS_PATH}
          element={
            isAdmin(user) ? (
              <SectionModal title="Play stats" wide pageScroll onClose={() => navigate(SECTION_PATH.admin)}>
                <StatsPage />
              </SectionModal>
            ) : (
              <Navigate to={SECTION_PATH.lobby} replace />
            )
          }
        />
        {/* Admin-only: the marketing shelf. Screenshots, brand files and the copy
            that is already live, so a listing or a post can be put together in
            one place. */}
        <Route
          path={ADMIN_MARKETING_PATH}
          element={
            isAdmin(user) ? (
              <SectionModal title="Marketing" wide pageScroll onClose={() => navigate(SECTION_PATH.admin)}>
                <MarketingPage />
              </SectionModal>
            ) : (
              <Navigate to={SECTION_PATH.lobby} replace />
            )
          }
        />
        {/* Content pages render as section-panels inside the Shell (like News) so the
            NavBar/lobby stay mounted and navigating between them is hiccup-free. They
            are ALSO prerendered to standalone static HTML for SEO/sharing (see
            scripts/prerender.mjs) — that only affects first load, not in-app nav. */}
        <Route
          path={SECTION_PATH.about}
          element={<SectionModal title="About" onClose={() => navigate(SECTION_PATH.lobby)}><AboutPage /></SectionModal>}
        />
        <Route
          path="/terms"
          element={<SectionModal title="Terms of Service" onClose={() => navigate(SECTION_PATH.lobby)}><TermsPage /></SectionModal>}
        />
        <Route
          path="/privacy"
          element={<SectionModal title="Privacy Policy" onClose={() => navigate(SECTION_PATH.lobby)}><PrivacyPage /></SectionModal>}
        />
        {/* Sign-in / account: floating modals, routed like the others. */}
        <Route path={LOGIN_PATH} element={<LoginModal onClose={() => navigate(SECTION_PATH.lobby)} />} />
        <Route path={ACCOUNT_PATH} element={<Navigate to={SECTION_PATH.profile} replace />} />
      </Route>
      <Route path={PLAY_PATH} element={renderPlayFlow()} />
      {/* Real online play (beta): a self-contained session driven by the game
          server. Separate from the local match flow above, which renders from an
          in-memory Match. */}
      <Route
        path="/online"
        element={
          <div className="page-main page-main-game">
            <RemoteGame onLeave={() => navigate(SECTION_PATH.lobby)} />
          </div>
        }
      />
      {/* A finished online match, by its id: what the profile's match list links to,
          and a link anyone can be sent. Full screen like any game page. */}
      <Route path="/replay" element={<div className="page-main page-main-game"><ReplayPage /></div>} />
      {/* Admin-only: a stored online match in the replay player, with its death
          forensics. Full screen like any game page, so outside the shell. */}
      <Route
        path="/admin/replays/watch"
        element={isAdmin(user) ? <div className="page-main page-main-game"><ReplayWatchPage /></div> : <Navigate to={SECTION_PATH.lobby} replace />}
      />
      {/* A public matchmaking box's own page (/game/duel|brawl|royale): the lobby's
          cards navigate here, the whole session lives here, and reloading the
          browser reconnects into the same match (see OnlineMatchPage). */}
      <Route path="/game/:mode" element={<OnlineMatchPage />} />
      {/* A ranked mode's own page (/ranked/duel | /ranked/ffa): the search, the
          round you play while it runs, the match-found banner, and the match. */}
      <Route path="/ranked/:mode" element={<RankedMatchPage />} />
      {/* Play Now (the endless drop-in room) and the Royale (sign-up, the wait,
          the knock-out): full screen, each on its own URL so a reload lands back in. */}
      <Route path="/play-now" element={<PlayNowPage />} />
      <Route path="/royale" element={<RoyalePage />} />
      {/* Debug-only: render the in-game / round-over / match-over screens with a
          mock match, for iterating layout at any width. Not linked in the UI. */}
      {/* Shop "try it": a solo match wearing the cosmetic, back to the shop on exit. */}
      <Route path="/shop/try/:kind/:id" element={<ShopTryPage />} />
      {/* Play Now's and the Royale's in-game screens with mock data (no server). */}
      <Route path="/debug/playnow-layer" element={<PlayNowLayerLab />} />
      <Route path="/debug/open-achievements" element={<OpenAchievementsLab />} />
      <Route path="/debug/royale-layer" element={<RoyaleLayerLab />} />
      <Route path="/debug/game" element={<DebugGame />} />
      <Route path="/debug/round-over" element={<DebugRoundOver />} />
      <Route path="/debug/match-over" element={<DebugMatchOver />} />
      <Route path="/debug/match-end" element={<DebugMatchEnd />} />
      <Route path="/debug/ranks" element={<DebugRanks />} />
      <Route path="/debug/rank-badges" element={<RankBadgesSheet />} />
      <Route path="/debug/royale-cards" element={<RoyaleCardsSheet />} />
      <Route path="/debug/who-plays" element={<WhoPlaysSheet />} />
      <Route path="/debug/phone-player" element={<PhonePlayerSheet />} />
      <Route path="/debug/royale-ux" element={<RoyaleUxSheet />} />
      <Route path="/debug/royale-flow" element={<RoyaleFlowSheet />} />
      <Route path="/debug/room-designs" element={<RoomDesignsSheet />} />
      <Route path="/debug/room-frames" element={<RoomFramesSheet />} />
      <Route path="/debug/matches" element={<DebugMatches />} />
      {/* Netcode debug harness: server + 2 clients + simulated network, 5 canvases. */}
      <Route path="/debug/bots" element={<DebugBotsPage />} />
      <Route path="/debug/net" element={<DebugNetPage />} />
      {/* Design-only: compare lobby / local setup layout variants. Not linked in the UI. */}
      <Route path="/playground" element={<PlaygroundPage />} />
      <Route path="/debug/ranked-lobby" element={<RankedLobbyLab />} />
      <Route path="/debug/duel-dots" element={<DuelDotsLab />} />
      <Route path="/debug/duel-board" element={<DuelBoardLab />} />
      {/* Start-page design lab: unlisted public link (shared with testers), noindex. */}
      <Route path="/start-designs" element={<React.Suspense fallback={null}><StartDesignsLab /></React.Suspense>} />
      <Route path="/debug/start-designs" element={<React.Suspense fallback={null}><StartDesignsLab /></React.Suspense>} />
      <Route path="/debug/sound-lab" element={<React.Suspense fallback={null}><SoundLab /></React.Suspense>} />
      <Route path="/debug/navbar-designs" element={<React.Suspense fallback={null}><NavbarLab /></React.Suspense>} />
      <Route path="/debug/brand-lab" element={<React.Suspense fallback={null}><BrandLab /></React.Suspense>} />
      <Route path="/debug/royale-end" element={<React.Suspense fallback={null}><RoyaleEndLab /></React.Suspense>} />
      <Route path="/debug/first-minute" element={<React.Suspense fallback={null}><FirstMinuteLab /></React.Suspense>} />
      <Route path="/debug/timings" element={<React.Suspense fallback={null}><TimingsLab /></React.Suspense>} />
      <Route path="/debug/royale-designs" element={<React.Suspense fallback={null}><RoyaleDesigns /></React.Suspense>} />
      <Route path="/debug/progression-designs" element={<React.Suspense fallback={null}><ProgressionDesigns /></React.Suspense>} />
      <Route path="/debug/today-designs" element={<React.Suspense fallback={null}><TodayDesigns /></React.Suspense>} />
      <Route path="/debug/progress-lab" element={<React.Suspense fallback={null}><ProgressLab /></React.Suspense>} />
      <Route path="/debug/portal-lab" element={<React.Suspense fallback={null}><PortalLab /></React.Suspense>} />
      <Route path="/debug/achievement-designs" element={<React.Suspense fallback={null}><AchievementDesigns /></React.Suspense>} />
      <Route path="/debug/worm-skins" element={<React.Suspense fallback={null}><WormSkinsLab /></React.Suspense>} />
      <Route path="/debug/crown-lab" element={<React.Suspense fallback={null}><CrownLab /></React.Suspense>} />
      <Route path="/debug/royale-card" element={<React.Suspense fallback={null}><RoyaleCardLab /></React.Suspense>} />
      <Route path="/debug/stage-notices" element={<React.Suspense fallback={null}><StageNotices /></React.Suspense>} />
      <Route path="/debug/duel-round-win" element={<DuelRoundWinLab />} />
      <Route path="/debug/ffa-finding" element={<FfaFindingLab />} />
      <Route path="/debug/ffa-banner" element={<FfaBannerLab />} />
      {/* Design-only: compare the 10 active-power-up status-bar designs. Not linked in the UI. */}
      <Route path="/fx" element={<EffectBarsDemo />} />
      {/* Design-only: compare power-up token designs (same icons). Not linked in the UI. */}
      <Route path="/pu" element={<PowerUpDesignsDemo />} />
      {/* Design-only: compare wall-hack wall effects. Not linked in the UI. */}
      <Route path="/wh" element={<WallHackDesignsDemo />} />
      {/* Design-only: compare power-up spawn/grab animations. Not linked in the UI. */}
      <Route path="/pa" element={<PowerUpAnimDemo />} />
      {/* Design-only: compare flag-display (player-row trigger) styles. Not linked in the UI. */}
      <Route path="/flagstyles" element={<FlagStylesDemo />} />
      {/* Design-only: compare start-page player-card + big-flag layouts. Not linked in the UI. */}
      <Route path="/playerbar" element={<PlayerBarDemo />} />
      <Route path="/sw" element={<SwitchAnimDemo />} />
      <Route path="/spawn" element={<SpawnAnimDemo />} />
      {/* Design-only: flag-fills-its-own-card variations. Not linked in the UI. */}
      <Route path="/flagcard" element={<FlagCardDemo />} />
      {/* Design-only: gallery of the purchasable novelty (shop) flags. Not linked in the UI. */}
      <Route path="/flagshop" element={<NoveltyFlagsDemo />} />
      <Route path="*" element={<Navigate to={SECTION_PATH.lobby} replace />} />
    </Routes>
    </>
  );
}
